/* 
  eslint-disable flights/no-use-effect-in-controller
  -- this line was auto generated, hence fix the issue timely
*/
import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { pricingUtils } from "@flights/web-api-utils-universal";
import { UISearchAiSummary, SearchAiSummaryRequestPayload, SearchAiSummaryV2RequestPayload } from "@flights/types";

import { actions as searchAiSummaryActions } from "../../../../store/searchAiSummary/actions";
import { getSearchCriteria } from "../../../../store/searchCriteria/selectors";
import { getSearchFetchStatus } from "../../../../store/searchResults/selectors";
import { getSearchFetchStatus as getSearchOutboundFetchStatus } from "../../../../store/searchResultsBounds/outboundSelectors";
import { getSearchFetchStatus as getSearchInboundFetchStatus } from "../../../../store/searchResultsBounds/inboundSelectors";
import useGetSearchResultsBound from "../../../../hooks/useGetSearchResultsBound";
import { clientFetch } from "@flights/client-fetch";
import useContextName from "../../../../hooks/useContextName";
import { useGetAvailableFilters } from "./boundsFilterHooks";

const useAiSummaryController = (endpointVersion: "v1" | "v2") => {
  const contextName = useContextName();
  const dispatch = useDispatch();
  const searchFetchStatus = useSearchFetchStatus();
  const { stops = [], airlines = [] } = useGetAvailableFilters();
  const { adults, children } = useSelector(getSearchCriteria);

  const requestPayload = useMemo(() => {
    const payload: SearchAiSummaryRequestPayload = {
      passengers: adults + children.length,
      stops: stops.map(({ numberOfStops, count, minPrice }) => {
        return {
          numberOfStops,
          count,
          minPrice: pricingUtils.priceToNumber(minPrice)
        };
      }),
      currencyCode: stops[0]?.minPrice.currencyCode || ""
    };

    if (endpointVersion === "v2") {
      (payload as SearchAiSummaryV2RequestPayload).topAirlines = airlines
        .slice(0, 3)
        .map(({ iataCode, name, count, minPrice }) => {
          return {
            iataCode,
            airlineName: name,
            offersCount: count,
            minPrice: pricingUtils.priceToNumber(minPrice)
          };
        });

      const directFlightsAggregation = stops.find(({ numberOfStops }) => numberOfStops === 0);
      (payload as SearchAiSummaryV2RequestPayload).cheapestDirectAirlineName =
        directFlightsAggregation?.cheapestAirline?.name;
    }

    return payload;
  }, [adults, children, stops, airlines, endpointVersion]);

  useEffect(() => {
    if (searchFetchStatus === "loading") {
      dispatch(searchAiSummaryActions.fetch());
    } else if (searchFetchStatus === "failed") {
      dispatch(searchAiSummaryActions.fetchError({ message: "Failed to fetch search results" }));
    } else if (searchFetchStatus === "success") {
      if (requestPayload.stops.length === 0 || requestPayload.passengers === 0) {
        dispatch(searchAiSummaryActions.fetchError({ message: "Empty search results" }));
      } else {
        dispatch(searchAiSummaryActions.fetch());

        const url = endpointVersion === "v2" ? "/api/search/ai-summary/v2" : "/api/search/ai-summary";

        clientFetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Flights-Context-Name": contextName
          },
          body: JSON.stringify(requestPayload)
        })
          .then((data: UISearchAiSummary) => {
            dispatch(searchAiSummaryActions.fetchSuccess(data));
          })
          .catch((error) => {
            dispatch(searchAiSummaryActions.fetchError(error));
          });
      }
    }
  }, [searchFetchStatus, JSON.stringify(requestPayload)]); // eslint-disable-line react-hooks/exhaustive-deps
};

const useSearchFetchStatus = () => {
  const bound = useGetSearchResultsBound();
  const searchRegularFetchStatus = useSelector(getSearchFetchStatus);
  const searchOutboundFetchStatus = useSelector(getSearchOutboundFetchStatus);
  const searchInboundFetchStatus = useSelector(getSearchInboundFetchStatus);

  const searchFetchStatus = useMemo(() => {
    if (bound === "outbound") {
      return searchOutboundFetchStatus;
    } else if (bound === "inbound") {
      return searchInboundFetchStatus;
    } else {
      return searchRegularFetchStatus;
    }
  }, [bound, searchInboundFetchStatus, searchOutboundFetchStatus, searchRegularFetchStatus]);

  return searchFetchStatus;
};

export default useAiSummaryController;
